import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);

  // Refs for Google Map
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);

    // Handle fade-in animations
    const fadeElements = document.querySelectorAll('.fade-in');

    // Add visible class to all fade-in elements
    fadeElements.forEach((element) => {
      element.classList.add('visible');
    });

    // Set up intersection observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all fade-in elements
    fadeElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      // Cleanup observer on component unmount
      fadeElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  // Initialize Google Maps
  useEffect(() => {
    // Load Google Maps script
    const loadGoogleMapsScript = () => {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      document.head.appendChild(script);
    };

    // Initialize the map
    const initMap = () => {
      if (!mapRef.current) return;

      // Custom map style
      const mapStyles = [
        {
          featureType: 'all',
          elementType: 'geometry',
          stylers: [{ color: '#f5f5f5' }],
        },
        {
          featureType: 'all',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#616161' }],
        },
        {
          featureType: 'all',
          elementType: 'labels.text.stroke',
          stylers: [{ color: '#f5f5f5' }],
        },
        {
          featureType: 'administrative.land_parcel',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#bdbdbd' }],
        },
        {
          featureType: 'water',
          elementType: 'geometry',
          stylers: [{ color: '#e9e9e9' }],
        },
        {
          featureType: 'water',
          elementType: 'labels.text.fill',
          stylers: [{ color: '#9e9e9e' }],
        },
      ];

      // Map options
      const mapOptions = {
        center: { lat: 37.7749, lng: -122.4194 }, // San Francisco
        zoom: 2,
        styles: mapStyles,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      };

      // Create map instance
      const map = new window.google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Office locations
      const locations = [
        { lat: 37.7749, lng: -122.4194, title: 'San Francisco Office' }, // San Francisco
        { lat: 51.5074, lng: -0.1278, title: 'London Office' }, // London
        { lat: 1.3521, lng: 103.8198, title: 'Singapore Office' }, // Singapore
      ];

      // Add markers for each location
      locations.forEach((location) => {
        // Create SVG marker icon
        const markerIcon = {
          path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z',
          fillColor: '#9c6eff',
          fillOpacity: 1,
          strokeWeight: 0,
          rotation: 0,
          scale: 2,
          anchor: new window.google.maps.Point(12, 22),
        };

        // Create marker
        const marker = new window.google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map,
          title: location.title,
          icon: markerIcon,
          animation: window.google.maps.Animation.DROP,
        });

        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `<div style="padding: 10px;"><strong>${location.title}</strong></div>`,
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      });
    };

    // Load the Google Maps script
    if (!window.google?.maps) {
      loadGoogleMapsScript();
    } else {
      initMap();
    }

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        // Clean up map instance if needed
      }
    };
  }, []);

  // Timeline animation effect
  useEffect(() => {
    const timelineItems = document.querySelectorAll('.timeline-item');
    const timelineMarkers = document.querySelectorAll('.timeline-marker');
    const timelineTitles = document.querySelectorAll('.value-title');
    const timelineContents = document.querySelectorAll('.timeline-content-right p');
    const totalItems = timelineItems.length;

    if (totalItems === 0) return;

    const animationInterval = setInterval(() => {
      // Remove active classes from all elements
      timelineMarkers.forEach((marker) => marker.classList.remove('active'));
      timelineItems.forEach((item) => item.classList.remove('active'));
      timelineTitles.forEach((title) => {
        title.style.color = '';
        title.style.opacity = '0.7';
      });
      timelineContents.forEach((content) => {
        content.style.opacity = '0.7';
        content.style.transform = 'scale(1)';
        content.style.color = '#666';
      });

      // Add active classes to current elements
      if (timelineMarkers[activeTimelineIndex]) {
        timelineMarkers[activeTimelineIndex].classList.add('active');
      }

      if (timelineItems[activeTimelineIndex]) {
        timelineItems[activeTimelineIndex].classList.add('active');
      }

      if (timelineTitles[activeTimelineIndex]) {
        timelineTitles[activeTimelineIndex].style.color = '#9c6eff';
        timelineTitles[activeTimelineIndex].style.opacity = '1';
      }

      if (timelineContents[activeTimelineIndex]) {
        timelineContents[activeTimelineIndex].style.opacity = '1';
        timelineContents[activeTimelineIndex].style.transform = 'scale(1.02)';
        timelineContents[activeTimelineIndex].style.color = '#333';
      }

      // Update active index for next iteration
      setActiveTimelineIndex((prevIndex) => (prevIndex + 1) % totalItems);
    }, 2000);

    // Cleanup interval on component unmount
    return () => clearInterval(animationInterval);
  }, [activeTimelineIndex]);

  return (
    <>
      <Helmet>
        <title>
          Flexible AI Chatbot Subscriptions |Simple, Transparent Pricing Plans| UK ,Singapore, UAE
        </title>
        <meta
          name="description"
          content="Flexible AI Chatbot Subscriptions |Simple, Transparent Pricing Plans| UK ,Singapore, UAE"
        />
        <meta
          name="keywords"
          content="conversational AI solutions, conversational AI, AI chatbot solutions, AI Insights, ai customer service, ai service"
        />
        <link rel="canonical" href="https://www.aichatassist.com/about" />
        <meta
          property="og:title"
          content="AI Chatbot & Conversational AI | AI Chat Assist Global"
        />
        <meta
          property="og:description"
          content="Engage customers 24/7 with AI Chat Assist's conversational AI solutions. Trusted in Singapore, UK & UAE for powerful, scalable chatbot automation."
        />
        <meta property="og:url" content="https://www.aichatassist.com/about" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta property="og:site_name" content="AI Chat Assist" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Chatbot & Conversational AI | AI Chat Assist Global"
        />
        <meta
          name="twitter:description"
          content="Engage customers 24/7 with AI Chat Assist's conversational AI solutions. Trusted in Singapore, UK & UAE for powerful, scalable chatbot automation."
        />
        <meta
          name="twitter:image"
          content="https://admin.aichatassist.com/uploads/Wordpress_Plugin_Banner_6964048f39.jpg"
        />
        <meta name="twitter:site" content="@aichatassist" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section about-page" style={{ marginTop: '-100px' }}>
        <div className="container text-center py-5 position-relative">
          <div className="row justify-content-center">
            <div className="col-lg-10 fade-in">
              <h1 className="display-4 fw-bold mb-3">
                Conversational AI Solutions
                <br />
                <span className="hero-highlight">Built for Impact</span>
              </h1>
              <p className="lead mb-4">
                Empowering global brands in Singapore, the UK, and UAE with intelligent AI chatbot
                solutions to transform customer engagement and service.
              </p>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-12 fade-in text-center">
              <img
                src="/images/aboutus.png"
                alt="Team of software engineers"
                className="img-fluid team-illustration"
              />
            </div>
          </div>
          <div className="hero-bg-elements"></div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-5 values-section text-light">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-8 mx-auto text-center fade-in">
              <h2 className="fw-bold mb-4">
                Our <span className="hero-highlight">Core Values</span>
              </h2>
              <p className="lead">The principles that guide everything we do</p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="timeline-container">
                {/* Single continuous timeline line */}
                <div className="timeline-line"></div>

                {/* Timeline items */}
                <div className="timeline-item fade-in">
                  <div className="timeline-content-left">
                    <h3 className="value-title">Innovation</h3>
                  </div>
                  <div className="timeline-dot-container">
                    <div className="timeline-marker"></div>
                  </div>
                  <div className="timeline-content-right">
                    <p>
                      We explore new technologies and tools. To provide new unique and cutting edge
                      solutions that set our clients apart in the digital landscape.
                    </p>
                  </div>
                </div>

                <div className="timeline-item fade-in">
                  <div className="timeline-content-left">
                    <h3 className="value-title">Human-Centered Design</h3>
                  </div>
                  <div className="timeline-dot-container">
                    <div className="timeline-marker"></div>
                  </div>
                  <div className="timeline-content-right">
                    <p>
                      We are focusing on understanding and meeting user needs at every stage. By
                      creating intuitive and engaging experiences, we ensure results are not only
                      aesthetically pleasing but also highly functional and user-friendly.
                    </p>
                  </div>
                </div>

                <div className="timeline-item fade-in">
                  <div className="timeline-content-left">
                    <h3 className="value-title">Quality</h3>
                  </div>
                  <div className="timeline-dot-container">
                    <div className="timeline-marker"></div>
                  </div>
                  <div className="timeline-content-right">
                    <p>
                      We are committed to excellence, adhering to the highest standards of quality
                      and performance. Using advanced technologies and approaches, we ensure every
                      project exceeds our clients' expectations.
                    </p>
                  </div>
                </div>

                <div className="timeline-item fade-in">
                  <div className="timeline-content-left">
                    <h3 className="value-title">Collaboration</h3>
                  </div>
                  <div className="timeline-dot-container">
                    <div className="timeline-marker"></div>
                  </div>
                  <div className="timeline-content-right">
                    <p>
                      As a small, close-knit team, we have worked together for years, developing a
                      deep understanding of each other's strengths and fostering strong teamwork.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-5">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-8 mx-auto text-center fade-in">
              <h2 className="fw-bold mb-4">Meet Our Leadership Team</h2>
              <p className="lead">The experienced professionals guiding our company</p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="card border-0 shadow-sm h-100">
                <img src="https://via.placeholder.com/300x300" className="card-img-top" alt="CEO" />
                <div className="card-body p-4 text-center">
                  <h5 className="fw-bold mb-1">Sarah Johnson</h5>
                  <p className="text-muted mb-3">CEO & Co-Founder</p>
                  <div className="social-icons">
                    <a href="#" className="social-icon">
                      <i className="bi bi-linkedin"></i>
                    </a>
                    <a href="#" className="social-icon">
                      <i className="bi bi-twitter"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="card border-0 shadow-sm h-100">
                <img src="https://via.placeholder.com/300x300" className="card-img-top" alt="CTO" />
                <div className="card-body p-4 text-center">
                  <h5 className="fw-bold mb-1">Michael Chen</h5>
                  <p className="text-muted mb-3">CTO & Co-Founder</p>
                  <div className="social-icons">
                    <a href="#" className="social-icon">
                      <i className="bi bi-linkedin"></i>
                    </a>
                    <a href="#" className="social-icon">
                      <i className="bi bi-github"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="card border-0 shadow-sm h-100">
                <img src="https://via.placeholder.com/300x300" className="card-img-top" alt="CPO" />
                <div className="card-body p-4 text-center">
                  <h5 className="fw-bold mb-1">David Rodriguez</h5>
                  <p className="text-muted mb-3">Chief Product Officer</p>
                  <div className="social-icons">
                    <a href="#" className="social-icon">
                      <i className="bi bi-linkedin"></i>
                    </a>
                    <a href="#" className="social-icon">
                      <i className="bi bi-twitter"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="card border-0 shadow-sm h-100">
                <img src="https://via.placeholder.com/300x300" className="card-img-top" alt="CMO" />
                <div className="card-body p-4 text-center">
                  <h5 className="fw-bold mb-1">Emily Patel</h5>
                  <p className="text-muted mb-3">Chief Marketing Officer</p>
                  <div className="social-icons">
                    <a href="#" className="social-icon">
                      <i className="bi bi-linkedin"></i>
                    </a>
                    <a href="#" className="social-icon">
                      <i className="bi bi-instagram"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Our Mission Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 fade-in">
              <h2 className="fw-bold mb-4">
                Our <span className="hero-highlight">Mission</span>
              </h2>
              <p className="lead mb-4">
                At AI Chat Assist, we’re redefining customer service through smart, scalable
                conversational AI that feels natural, intuitive, and genuinely helpful.
              </p>
              <p className="lead mb-4">
                Our conversational AI solutions are designed to enhance human connection —
                delivering seamless ai customer service, boosting engagement, and building trust at
                every touchpoint.
              </p>
            </div>
            <div className="col-lg-6 fade-in">
              <div className="row g-4">
                <div className="col-md-12 mb-4">
                  <div className="card border-0 shadow-sm hover-lift h-100">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center">
                        <div className="me-4">
                          <div
                            className="d-flex align-items-center justify-content-center bg-primary rounded-circle"
                            style={{ width: '60px', height: '60px' }}
                          >
                            <i className="bi bi-lightbulb text-white fs-4"></i>
                          </div>
                        </div>
                        <div>
                          <h5 className="fw-bold mb-2">Innovation-Driven</h5>
                          <p className="mb-0">
                            We continuously evolve our AI chatbot solutions by leveraging
                            cutting-edge technologies and AI Insights.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="card border-0 shadow-sm hover-lift h-100">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center">
                        <div className="me-4">
                          <div
                            className="d-flex align-items-center justify-content-center bg-primary rounded-circle"
                            style={{ width: '60px', height: '60px' }}
                          >
                            <i className="bi bi-people text-white fs-4"></i>
                          </div>
                        </div>
                        <div>
                          <h5 className="fw-bold mb-2">Customer-Focused</h5>
                          <p className="mb-0">
                            Every ai service we build is tailored to enhance customer experience and
                            operational efficiency.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-5">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-8 mx-auto text-center fade-in">
              <h2 className="fw-bold mb-4">
                Our <span className="hero-highlight">Approach</span>
              </h2>
              <p className="lead">
                {' '}
                How we design, build, and optimize conversational AI solutions for business growth
              </p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-4 fade-in">
              <div className="card h-100 border-0 shadow-sm hover-lift">
                <div className="card-body p-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-primary-soft rounded-circle mb-3"
                    style={{ width: '60px', height: '60px' }}
                  >
                    <i className="bi bi-search fs-4 text-white"></i>
                  </div>
                  <h4 className="fw-bold mb-3">Discovery</h4>
                  <p>
                    We start by diving deep into your brand, audience, and support goals. This helps
                    us shape conversational AI that fits your exact customer journey and use cases.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 fade-in">
              <div className="card h-100 border-0 shadow-sm hover-lift">
                <div className="card-body p-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-primary-soft rounded-circle mb-3"
                    style={{ width: '60px', height: '60px' }}
                  >
                    <i className="bi bi-gear fs-4 text-white"></i>
                  </div>
                  <h4 className="fw-bold mb-3">Development</h4>
                  <p>
                    Our team of specialists crafts tailored AI chatbot solutions using proven
                    frameworks, prioritizing human-centered design, ai service reliability, and
                    contextual intelligence.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4 fade-in">
              <div className="card h-100 border-0 shadow-sm hover-lift">
                <div className="card-body p-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-primary-soft rounded-circle mb-3"
                    style={{ width: '60px', height: '60px' }}
                  >
                    <i className="bi bi-graph-up text-white fs-4"></i>
                  </div>
                  <h4 className="fw-bold mb-3">Optimization</h4>
                  <p>
                    With every customer interaction, we gather AI Insights to refine your chatbot
                    performance — improving accuracy, automation, and conversion continuously.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Technology Section */}
      <section className="py-5 text-white">
        <div className="container">
          <div className="row mb-5">
            <div className="col-md-8 mx-auto text-center fade-in">
              <h2 className="fw-bold mb-4">Technology Behind Our Conversational AI</h2>
              <p className="lead">
                AI Chat Assist integrates the most powerful technologies to deliver intelligent,
                secure, and scalable AI chatbot solutions.
              </p>
            </div>
          </div>
          <div className="row g-4 justify-content-center">
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="text-center p-4 bg-white rounded-4 h-100 tech-card">
                <div className="tech-icon mb-3">
                  <i className="bi bi-cpu fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2">Natural Language Processing</h5>
                <p className="mb-0">
                  {' '}
                  Our advanced NLP models understand and respond to complex human queries, powering
                  personalized ai customer service at scale.
                </p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="text-center p-4 bg-white rounded-4 h-100 tech-card">
                <div className="tech-icon mb-3">
                  <i className="bi bi-braces fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2">Machine Learning</h5>
                <p className="mb-0">
                  With machine learning at the core, we gather continuous AI Insights to evolve your
                  conversational AI solutions with every interaction.
                </p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="text-center p-4 bg-white rounded-4 h-100 tech-card">
                <div className="tech-icon mb-3">
                  <i className="bi bi-globe fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2">Multilingual Support</h5>
                <p className="mb-0">
                  Communicate globally with customers in their preferred language while maintaining
                  consistent ai service quality.
                </p>
              </div>
            </div>
            <div className="col-md-3 col-sm-6 fade-in">
              <div className="text-center p-4 bg-white rounded-4 h-100 tech-card">
                <div className="tech-icon mb-3">
                  <i className="bi bi-shield-check fs-1"></i>
                </div>
                <h5 className="fw-bold mb-2">Enterprise Security</h5>
                <p className="mb-0">
                  {' '}
                  All AI systems are protected by robust data encryption and compliance protocols to
                  ensure customer trust and safety.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
